/**
 * SL Transport API.
 *
 * Open, no key, and it answers with `access-control-allow-origin: *` -- which
 * is the whole reason this app has no backend. The browser talks to SL
 * directly; there is nothing in between to deploy, cache or keep patched.
 *
 * Docs: https://www.trafiklab.se/api/our-apis/sl/transport/
 * No API key. Five endpoints; this app uses /sites and /sites/{id}/departures.
 */

import { parseStockholm } from './time.ts';

const BASE = 'https://transport.integration.sl.se/v1';

/** Bus, metro, train, tram, ship. SL's own vocabulary, not ours. */
export type TransportMode = 'BUS' | 'METRO' | 'TRAIN' | 'TRAM' | 'SHIP' | 'FERRY';

/**
 * 1 and 2 are the two ends of a line. Which is which is arbitrary per line and
 * only meaningful next to the destination name, which is why a saved selection
 * stores both.
 */
export type DirectionCode = 1 | 2;

export type Deviation = {
  importance_level: number;
  /** CANCELLED matters; INFORMATION is lifts, works, and so on. */
  consequence?: 'CANCELLED' | 'INFORMATION' | string;
  message: string;
};

export type Departure = {
  destination: string;
  direction_code: DirectionCode;
  direction: string;
  /** EXPECTED, ATSTOP, CANCELLED. */
  state: string;
  /**
   * SL's own rendering: "Nu", "3 min", or -- for a cancelled or distant
   * departure -- a clock time like "09:22". Two formats behind one field, so
   * treat it as an opaque label rather than something to parse.
   */
  display: string;
  /** Local Stockholm time, with no zone suffix. See minutesLate(). */
  scheduled: string;
  expected: string;
  journey?: { id: number; state: string; prediction_state?: string };
  stop_area?: { id: number; name: string; type: string };
  /** `designation` is the platform or stand letter -- "J", "2". */
  stop_point?: { id: number; name: string; designation?: string };
  line: {
    id: number;
    designation: string;
    transport_mode: TransportMode;
    transport_authority_id?: number;
  };
  deviations?: Deviation[];
};

export type StopDeviation = {
  id: number;
  importance_level: number;
  message: string;
  scope?: unknown;
};

export type DeparturesResponse = {
  departures: Departure[];
  stop_deviations: StopDeviation[];
};

/**
 * Something went wrong talking to SL.
 *
 * `retryable` separates failures worth another request -- a timeout, a dropped
 * connection, SL's quota, a 5xx -- from ones where asking again only spends a
 * request that was never going to work. A 404 for a stop that no longer
 * exists will still be a 404 in two seconds.
 */
export class SlError extends Error {
  readonly status: number;
  /** How long the server asked us to wait, when it said. */
  readonly retryAfterMs: number | null;

  constructor(message: string, status: number, retryAfterMs: number | null = null) {
    super(message);
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }

  /** 0 means a timeout or a dropped connection, which is always worth retrying. */
  get retryable(): boolean {
    return (
      this.status === 0 ||
      this.status === 408 ||
      this.status === 429 ||
      this.status >= 500
    );
  }

  /** Told apart from an outage in the UI: nothing is broken, we just have to wait. */
  get isQuota(): boolean {
    return this.status === 429;
  }
}

/**
 * What to tell someone when a request has failed for good.
 *
 * Only reached once the retries are exhausted, so every message can say what
 * happens next without lying: if we are still going to try again, it says so,
 * and if we are not -- a stop that no longer exists -- it does not.
 */
export function describeError(error: unknown): string {
  if (!(error instanceof SlError)) return 'Något gick fel. Försöker igen.';
  if (error.isQuota)
    return 'SL:s anropskvot är slut just nu. Försöker igen om en stund.';
  if (error.status === 0) return 'Ingen kontakt med SL. Försöker igen.';
  if (error.status === 404) return 'Hållplatsen finns inte längre hos SL.';
  return `SL svarade ${error.status}. Försöker igen.`;
}

/**
 * A request that never comes back is worse than one that fails.
 *
 * Without this a stalled connection leaves the board spinning indefinitely --
 * no error, no retry, no times. Ten seconds is far beyond SL's normal
 * response: measured over 60 requests, p50 was 0.04s and the slowest 0.32s.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * SL's quota is per IP, so every stop on the board shares one budget and a
 * 429 for one of them means all of them are over it. Retrying per stop would
 * spend what little is left on requests that are certain to be refused, so
 * the first 429 puts the whole app on hold.
 *
 * Measured while depleted, roughly one request in eight was let through at
 * 5s intervals -- about one per 40 seconds -- and CloudFront sends no
 * Retry-After to go on, so the wait is a guess. It is used only when the
 * server does not say.
 */
const QUOTA_COOLDOWN_MS = 45_000;
let quotaBlockedUntil = 0;

const quotaRemainingMs = () => Math.max(0, quotaBlockedUntil - Date.now());

/** Seconds, or an HTTP date. Absent on SL's own 429s, but free to honour. */
function retryAfterMs(res: Response): number | null {
  const header = res.headers.get('retry-after');
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const when = Date.parse(header);
  return Number.isFinite(when) ? Math.max(0, when - Date.now()) : null;
}

/** The caller's signal, plus a deadline of our own. */
function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const timeout = AbortSignal.timeout(ms);
  if (!signal) return timeout;
  return typeof AbortSignal.any === 'function'
    ? AbortSignal.any([signal, timeout])
    : signal;
}

/**
 * How far ahead the board looks, in minutes.
 *
 * SL returns at most three departures per line and direction whatever this
 * is set to, so widening the window costs almost nothing -- Odenplan's board
 * is 43.8 KB at 60 minutes and 45.3 KB at 180 -- while a line running every
 * 40 minutes goes from one row of times to three. Beyond about 30 minutes SL
 * renders `display` as a clock time rather than a countdown, which is the
 * right way to show something that distant anyway.
 */
export const FORECAST_MINUTES = 180;

/**
 * One extra request, when a line you track disappears from an answer.
 *
 * This once retried any empty answer too, on the strength of a measurement
 * that turned out to be wrong: those "empty" responses were HTTP 429 quota
 * errors whose body is `{"error": "Quota has been exceeded"}`, and the script
 * that found them read `.get("departures", [])` off the error body without
 * ever checking the status. A real empty answer means a genuinely quiet stop,
 * and asking again changes nothing -- it only spends a request from a quota
 * that, as it turns out, is the scarce thing here.
 *
 * What survives is the narrower case, which does not depend on that
 * measurement: a line that was running a minute ago and has vanished from an
 * otherwise populated answer. See looksIncomplete() below.
 */
const RETRIES = 1;
const RETRY_MS = 400;

/**
 * For each site, which of the saved lines were actually running the last time
 * SL gave a usable answer.
 *
 * This is what makes it possible to spot a *truncated* response rather than
 * only a wholly empty one. Sampling caught SL returning 7 departures where a
 * stop normally returns 67 -- enough to look like a real answer, with most of
 * the board quietly missing. Nothing in the payload says it is short.
 *
 * A saved line that was running a minute ago and has now vanished is far
 * more likely to be that than a timetable change, so it is worth another
 * request. A line that was already absent last time is presumably just not
 * running, and asking again would achieve nothing -- which is the difference
 * between this and retrying whenever anything is missing. The latter would
 * retry on every refresh, all night, for anyone who saves a rush-hour line.
 *
 * Deliberately in memory only: it is evidence about the last few seconds,
 * and a reload should start from what SL says now.
 */
const runningAtSite = new Map<number, Set<string>>();

/**
 * Whether an answer is missing something it ought to contain.
 *
 * With no history to compare against -- a cold start -- the bar is that none
 * of the saved lines appeared at all, in an answer that did carry other
 * departures. That is weak evidence on its own, but it costs one request to
 * check and the alternative is a board that opens with your lines missing.
 */
function looksIncomplete(
  siteId: number,
  result: DeparturesResponse,
  expect: Pair[],
): boolean {
  // An empty answer is taken at face value: it means a quiet stop, not a bad
  // response. See the note on RETRIES for why this used to say otherwise.
  if (result.departures.length === 0) return false;
  if (expect.length === 0) return false;

  const missing = expect.filter((p) => !result.departures.some((d) => matches(d, p)));
  if (missing.length === 0) return false;

  const running = runningAtSite.get(siteId);
  if (!running) return missing.length === expect.length;

  return missing.some((p) => running.has(pairKey(p)));
}

/**
 * Record which saved lines this answer contained.
 *
 * Updated even when the answer was still incomplete after retrying, so a line
 * that has genuinely stopped for the night costs two extra requests once, at
 * the moment it stops, rather than on every refresh until morning.
 *
 * An empty answer teaches nothing about the timetable, so it is not allowed
 * to erase what we knew.
 */
function remember(siteId: number, result: DeparturesResponse, expect: Pair[]): void {
  if (result.departures.length === 0 || expect.length === 0) return;
  runningAtSite.set(
    siteId,
    new Set(
      expect
        .filter((p) => result.departures.some((d) => matches(d, p)))
        .map((p) => pairKey(p)),
    ),
  );
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason as Error);
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason as Error);
      },
      { once: true },
    );
  });
}

async function requestDepartures(
  siteId: number,
  forecast: number,
  signal?: AbortSignal,
): Promise<DeparturesResponse> {
  const url = `${BASE}/sites/${siteId}/departures?forecast=${Math.min(forecast, 1200)}`;

  // Already known to be over quota: fail without spending a request on it.
  const blocked = quotaRemainingMs();
  if (blocked > 0) {
    throw new SlError('SL:s anropskvot är slut just nu.', 429, blocked);
  }

  let res: Response;
  try {
    res = await fetch(url, { signal: withTimeout(signal, REQUEST_TIMEOUT_MS) });
  } catch (error) {
    // The caller changing its mind is not a failure to report. Anything else
    // reaching here is a dropped connection or our own deadline, and both are
    // worth another go.
    if (signal?.aborted) throw error;
    throw new SlError('SL svarade inte.', 0);
  }

  if (res.status === 429) {
    const wait = retryAfterMs(res) ?? QUOTA_COOLDOWN_MS;
    quotaBlockedUntil = Date.now() + wait;
    throw new SlError('SL:s anropskvot är slut just nu.', 429, wait);
  }

  if (!res.ok) {
    throw new SlError(
      res.status === 404
        ? 'Hållplatsen finns inte längre.'
        : `SL svarade ${res.status}.`,
      res.status,
      retryAfterMs(res),
    );
  }

  // A request got through, so whatever the budget was, it is not spent.
  quotaBlockedUntil = 0;

  const body = (await res.json()) as DeparturesResponse;
  return {
    departures: body.departures ?? [],
    stop_deviations: body.stop_deviations ?? [],
  };
}

/**
 * Departures for one site.
 *
 * Deliberately unfiltered. SL accepts `line` and `direction` parameters, but
 * they take one line each -- so filtering server-side would mean one request
 * per saved pair instead of one per stop. A stop's whole board arrives in a
 * single round trip; the selecting happens in matches() below.
 */
export async function fetchDepartures(
  siteId: number,
  {
    forecast = FORECAST_MINUTES,
    expect = [],
    signal,
  }: { forecast?: number; expect?: Pair[]; signal?: AbortSignal } = {},
): Promise<DeparturesResponse> {
  let result = await requestDepartures(siteId, forecast, signal);

  for (
    let attempt = 0;
    attempt < RETRIES && looksIncomplete(siteId, result, expect);
    attempt++
  ) {
    await delay(RETRY_MS, signal);
    result = await requestDepartures(siteId, forecast, signal);
  }

  remember(siteId, result, expect);
  return result;
}

/** A line running in one direction from one stop -- what you actually select. */
export type Pair = {
  mode: TransportMode;
  /** The number on the front of the vehicle: "4", "43X". */
  line: string;
  /**
   * null means "either direction".
   *
   * The picker always produces 1 or 2, because it read them off a real
   * departure. A line added by hand cannot: if it is not running inside the
   * 20-hour window there is nothing to read a direction code off, and
   * guessing gives a pair that silently never matches anything. So a
   * hand-added line matches on the number alone and shows both directions.
   */
  directionCode: DirectionCode | null;
  /** Where it says it is going, kept so the board can render before any fetch. */
  direction: string;
  /**
   * The stand or platform letter, snapshotted for the same reason as
   * `direction`: so a row is its final height on first paint.
   *
   * Without it the board draws one-line rows, then grows every one of them by
   * 16.5px when the departures land -- about 150px of the page moving under
   * your thumb. Reserving the space unconditionally instead would leave a
   * permanent blank strip at the many smaller stops that have no stands at
   * all, so the row has to know which kind of stop it belongs to before it
   * can be drawn. Undefined means "this stop does not use them".
   *
   * Reconciled against live data on every load, so a stand that changes
   * corrects itself and stops saved before this existed fill in by themselves.
   */
  stand?: string;
};

export const pairKey = (p: Pick<Pair, 'line' | 'directionCode'>) =>
  `${p.line}:${p.directionCode ?? 'any'}`;

export const matches = (d: Departure, p: Pick<Pair, 'line' | 'directionCode'>) =>
  d.line.designation === p.line &&
  (p.directionCode === null || d.direction_code === p.directionCode);

/**
 * Every distinct line + direction leaving a stop, for the setup screen's
 * picker. There is no endpoint that lists a stop's lines, so this reads them
 * off a long-window departure board instead.
 *
 * The limitation that follows: a line only appears if it runs within the
 * window. Twenty hours covers all but the genuinely rare -- a line that runs
 * weekday mornings only, looked up late on a Saturday, can be missing.
 */
export function pairsAtSite(departures: Departure[]): Pair[] {
  const byKey = new Map<string, Pair>();

  for (const d of departures) {
    const key = `${d.line.transport_mode}:${pairKey({
      line: d.line.designation,
      directionCode: d.direction_code,
    })}`;
    if (byKey.has(key)) continue;
    byKey.set(key, {
      mode: d.line.transport_mode,
      line: d.line.designation,
      directionCode: d.direction_code,
      direction: d.direction.trim(),
      ...(d.stop_point?.designation ? { stand: d.stop_point.designation } : {}),
    });
  }

  return [...byKey.values()].sort(
    (a, b) =>
      modeOrder(a.mode) - modeOrder(b.mode) ||
      // Numeric where possible, so 4 sorts before 17 and "43X" lands near 43.
      (parseInt(a.line, 10) || 0) - (parseInt(b.line, 10) || 0) ||
      a.line.localeCompare(b.line, 'sv') ||
      // Only pairs read off real departures reach here, so both codes are
      // set; 0 keeps a hand-added pair from reordering the list if one ever
      // does.
      (a.directionCode ?? 0) - (b.directionCode ?? 0),
  );
}

const MODE_ORDER: TransportMode[] = ['BUS', 'METRO', 'TRAIN', 'TRAM', 'SHIP', 'FERRY'];
const modeOrder = (m: TransportMode) => {
  const i = MODE_ORDER.indexOf(m);
  return i === -1 ? MODE_ORDER.length : i;
};

export const MODE_LABEL: Record<string, string> = {
  BUS: 'Buss',
  METRO: 'Tunnelbana',
  TRAIN: 'Pendeltåg',
  TRAM: 'Spårväg',
  SHIP: 'Båt',
  FERRY: 'Färja',
};

export const isCancelled = (d: Departure) =>
  d.state === 'CANCELLED' ||
  (d.deviations ?? []).some((v) => v.consequence === 'CANCELLED');

/** Deviations worth surfacing on a row: anything that is not a cancellation,
 *  since the cancellation already shows as struck-through text. */
export const notices = (d: Departure) =>
  (d.deviations ?? []).filter((v) => v.consequence !== 'CANCELLED');

/**
 * Minutes late, or 0 when running to time.
 *
 * Both timestamps go through the same Stockholm-anchored parse as everything
 * else, which keeps this right across the two nights a year when a local
 * reading of them would differ by an hour.
 */
export function minutesLate(d: Departure): number {
  if (!d.expected || !d.scheduled) return 0;
  const diff = parseStockholm(d.expected) - parseStockholm(d.scheduled);
  if (!Number.isFinite(diff)) return 0;
  return Math.round(diff / 60_000);
}
